import BaseController from "sap/mcsuite/controller/BaseController";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";

export type FilterArgument = {
	label?: string; // Optional label for the filter argument
	// This can be used for UI display purposes
	column: string;
	operator: string;
	value1: string | number | boolean;
	value2?: string | number | boolean; // Optional for operators like BT (Between)
};

export default class FilterUtils {
	public static async addLabel(
		controller: BaseController,
		param: FilterArgument,
		fieldLabel: string,
	): Promise<FilterArgument> {
		if (!param.label) {
			param.label = await this.getLabelForOperator(
				controller,
				fieldLabel ? fieldLabel : param.column,
				param.operator,
				param.value1.toString(),
				param.value2 ? param.value2.toString() : undefined,
			);
		}
		return param;
	}

	public static buildFilter(
		params: FilterArgument[],
		useAnd: boolean = true,
	): Filter {
		const filters: Filter[] = params.map((arg) => {
			return new Filter({
				path: arg.column,
				operator: this.parseOperator(arg.operator), // Default to EQ if operator is not found
				value1: arg.value1,
				value2: arg.value2 ? arg.value2 : undefined, // Only include value2 if it exists
				caseSensitive: false, // Default to case insensitive
			});
		});
		return new Filter(filters, useAnd); // Use the 'useAnd' parameter for logic
	}

	private static parseOperator(operator: string): FilterOperator {
		if (operator.toLowerCase() === "contains") {
			return FilterOperator.Contains;
		} else {
			return (
				FilterOperator[operator.toUpperCase() as keyof typeof FilterOperator] ||
				FilterOperator.EQ
			); // Default to EQ if operator is not found
		}
	}

	private static async getLabelForOperator(
		controller: BaseController,
		column: string,
		operator: string,
		value1: string,
		value2?: string,
	): Promise<string> {
		switch (operator.toLowerCase()) {
			case "contains": {
				const containsLabel = await controller.translator.Label.translate.bind(
					controller,
				)("LITERAL", "CONTAINS", "enthält");
				return `${column} ${containsLabel} "${value1}"`;
			}
			case "eq":
				return `${column} = "${value1}"`;
			case "ne":
				return `${column} ≠ "${value1}"`;
			case "lt":
				return `${column} < "${value1}"`;
			case "le":
				return `${column} ≤ "${value1}"`;
			case "gt":
				return `${column} > "${value1}"`;
			case "ge":
				return `${column} ≥ "${value1}"`;
			case "bt": {
				const betweenLabel = await controller.translator.Label.translate.bind(
					controller,
				)("LITERAL", "BETWEEN", "zwischen");
				return value2
					? `${column} ${betweenLabel} "${value1}", "${value2}"`
					: "Between";
			}
			default:
				return operator; // Return the operator as is if no label is found
		}
	}
}
